import { useCallback, useEffect } from 'react'

import { useOnboarding } from '@/context/OnboardingContext'
import { useTypedDispatch, useTypedSelector } from '@/store/hooks'
import { clearPaymentInfo, createCharge, startPayment } from '@/store/payment'
import { getPaymentCharge } from '@/store/selectors'
import { PM_PAYMENT_EVENT } from '@/utils/postMessage/constants'
import { calculateUpdatedChargePrice } from '@/utils/products'

import { PaymentIntent } from '@/store/payment/interfaces'
import { connection } from '@/utils/postMessage'

type Props = {
  isOpen: boolean
  onModalClose?: () => void
}

export const usePostMessagePurchaseEvents = ({
  isOpen,
  onModalClose,
}: Props) => {
  const dispatch = useTypedDispatch()
  const charge = useTypedSelector(getPaymentCharge)
  const { checkOnboarding } = useOnboarding()

  const receiveMessage = useCallback(
    (eventName: string, data: unknown) => {
      switch (eventName) {
        case PM_PAYMENT_EVENT.TRANSACTION_DECLINED: {
          dispatch(clearPaymentInfo())
          onModalClose?.()
          break
        }

        case PM_PAYMENT_EVENT.TRANSACTION_ACCEPTED: {
          dispatch(startPayment.fulfilled({ appId: charge?.appId }))
          dispatch(clearPaymentInfo())
          break
        }

        case PM_PAYMENT_EVENT.TRANSACTION_FAILED: {
          dispatch(
            startPayment.rejected({
              error: (data as { error?: unknown })?.error,
            })
          )
          break
        }

        case PM_PAYMENT_EVENT.CHECK_ONBOARDING: {
          checkOnboarding()
          break
        }

        case PM_PAYMENT_EVENT.UPDATE_BILLING_CYCLE: {
          const { paymentIntent, billingCycle } = data as {
            paymentIntent: PaymentIntent
            billingCycle: number
          }

          const price = calculateUpdatedChargePrice(
            paymentIntent.price,
            billingCycle
          )

          const updatedPaymentIntent = {
            ...paymentIntent,
            months: billingCycle,
            price,
          }

          dispatch(createCharge({ paymentIntent: updatedPaymentIntent }))
          break
        }

        default:
          break
      }
    },
    [charge, onModalClose, checkOnboarding, dispatch]
  )

  useEffect(() => {
    if (!isOpen) {
      return
    }

    connection.events.on('*', receiveMessage)
    return () => {
      connection.events.off('*', receiveMessage)
    }
  }, [receiveMessage, isOpen])

  return null
}
